import React, { useState, useMemo } from 'react';
import { Search, MessageSquare } from 'lucide-react';
import { format, parse } from 'date-fns';
import { tr } from 'date-fns/locale';
import { CHANNEL_TR } from '../i18n/tr';
import PageHeader from './ui/PageHeader';
import EmptyState from './ui/EmptyState';
import StatusBadge from './ui/StatusBadge';
import AppointmentDetailModal from './ui/AppointmentDetailModal';

const DATE_FMT = 'yyyy-MM-dd';

const displayPhone = (call) => {
  if (call.channel && CHANNEL_TR[call.channel]) {
    return call.appointment?.customerPhone || CHANNEL_TR[call.channel];
  }
  return call.phoneNumber || '—';
};

const customerName = (call) => {
  const a = call.appointment;
  if (!a) return '—';
  return a.customerName || `${a.firstName || ''} ${a.lastName || ''}`.trim() || '—';
};

const formatApptDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return format(parse(dateStr, DATE_FMT, new Date()), 'd MMM yyyy', { locale: tr });
  } catch {
    return dateStr;
  }
};

/**
 * Admin-only page: full conversation records with standardized detail modal on click.
 * Reuses the same callLogs data source as Çağrı Kayıtları.
 */
const AdminConversationDetails = ({ callLogs = [] }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCall, setSelectedCall] = useState(null);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return callLogs;
    return callLogs.filter((call) => {
      const a = call.appointment;
      const haystack = [
        displayPhone(call),
        customerName(call),
        call.serviceType,
        call.status,
        call.summary,
        a?.plate,
        a?.date,
        call.transcript,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [callLogs, searchTerm]);

  return (
    <div className="page-container">
      <PageHeader
        title="Görüşme Detayları"
        description="Tamamlanan tüm görüşmelerin detaylı kayıtları ve transkriptleri"
      />

      <div className="card mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Telefon, müşteri, plaka, özet veya transkript ara…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="field w-full pl-10"
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon={MessageSquare}
          title="Görüşme kaydı yok"
          description={
            searchTerm
              ? 'Arama kriterlerinize uygun görüşme bulunamadı.'
              : 'Henüz kayıtlı görüşme bulunmuyor.'
          }
        />
      ) : (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead>
                <tr className="border-b border-white/10 bg-white/[0.02] text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3 font-medium">Tarih / Zaman</th>
                  <th className="px-4 py-3 font-medium">Telefon</th>
                  <th className="px-4 py-3 font-medium">Müşteri</th>
                  <th className="px-4 py-3 font-medium">İşlem</th>
                  <th className="px-4 py-3 font-medium">Durum</th>
                  <th className="px-4 py-3 font-medium">Randevu</th>
                  <th className="px-4 py-3 font-medium">Plaka</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((call) => (
                  <tr
                    key={call.id}
                    onClick={() => setSelectedCall(call)}
                    className="cursor-pointer border-b border-white/5 transition-colors hover:bg-white/[0.04]"
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-slate-200">
                      {format(new Date(call.callTime), 'd MMM yyyy HH:mm', { locale: tr })}
                    </td>
                    <td className="px-4 py-3 text-slate-300">{displayPhone(call)}</td>
                    <td className="px-4 py-3 text-slate-300">{customerName(call)}</td>
                    <td className="px-4 py-3 text-slate-300">{call.serviceType || '—'}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={call.status} />
                    </td>
                    <td className="px-4 py-3 text-slate-400">
                      {call.appointment?.date
                        ? `${formatApptDate(call.appointment.date)} ${call.appointment.time || ''}`.trim()
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-300">{call.appointment?.plate || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedCall && (
        <AppointmentDetailModal
          call={selectedCall}
          title="Görüşme detayı"
          onClose={() => setSelectedCall(null)}
        />
      )}
    </div>
  );
};

export default AdminConversationDetails;
